import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { App as CapacitorApp } from '@capacitor/app';
import { captureCampaignContextFromUrl } from './analytics';

/**
 * Maps an incoming universal link to an in-app route. Links are shared in path form
 * (`/team/x`) but may still arrive in legacy hash form (`/#/team/x`).
 */
export const toAppRoute = (rawUrl: string): string | null => {
  try {
    const url = new URL(rawUrl);
    if (url.hostname === 'appclip.apple.com') {
      const bundleIds = url.searchParams.getAll('p');
      return url.protocol === 'https:' && !url.username && !url.password && !url.port
        && url.pathname === '/id' && !url.href.includes('#')
        && bundleIds.length === 1 && bundleIds[0] === 'com.mattspeller.clubroots.Clip'
        ? '/team/demo/result-20260912-Church_Royals'
        : null;
    }
    const route = url.hash
      ? url.hash.replace(/^#/, '')
      : `${url.pathname}${url.search}`;
    if (!route || route === '/') return null;
    if (route.startsWith('/attendance/') || route.startsWith('/a/') || route.startsWith('/v/')) return null;
    return route.startsWith('/') ? route : `/${route}`;
  } catch {
    return null;
  }
};

/** Routes universal links to the matching screen when the app is already installed. */
export const useDeepLinks = () => {
  const navigate = useNavigate();
  const launchUrlHandled = useRef(false);

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    let removeListener: (() => void) | undefined;
    let cancelled = false;

    const openUrl = (url: string) => {
      captureCampaignContextFromUrl(url);
      const route = toAppRoute(url);
      if (route) navigate(route);
    };

    CapacitorApp.addListener('appUrlOpen', ({ url }) => {
      if (cancelled) return;
      launchUrlHandled.current = true;
      openUrl(url);
    })
      .then(handle => {
        if (cancelled) handle.remove();
        else removeListener = () => handle.remove();
      })
      .catch(err => console.error('Failed to register deep link listener:', err));

    if (!launchUrlHandled.current) {
      CapacitorApp.getLaunchUrl()
        .then(launch => {
          if (cancelled || launchUrlHandled.current) return;
          launchUrlHandled.current = true;
          if (launch?.url) openUrl(launch.url);
        })
        .catch(err => console.error('Failed to read deep link launch URL:', err));
    }

    return () => {
      cancelled = true;
      removeListener?.();
    };
  }, [navigate]);
};
